import { Component, Input, OnInit, OnChanges, SimpleChanges, ViewChild, Output, EventEmitter } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort, Sort } from '@angular/material/sort';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { ColumnConfig } from '../../models/column-config.interface';

@Component({
  selector: 'shared-table',
  templateUrl: './shared-table.component.html',
  styleUrls: ['./shared-table.component.scss']
})
export class SharedTableComponent<T = any> implements OnInit, OnChanges {
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(MatPaginator) paginator: MatPaginator;

  @Input() columns: ColumnConfig<T>[] = [];
  @Input() data: T[] = [];
  @Input() loading = false;
  @Input() skeletonRows = 5;
  @Input() sortFn?: (a: T, b: T, column: string) => number;

  // Pagination inputs
  @Input() showPagination = false;
  @Input() pageSize = 10;
  @Input() pageSizeOptions: number[] = [5, 10, 25, 50];
  @Input() totalCount = 0;

  // Event outputs
  @Output() sortChange = new EventEmitter<Sort>();
  @Output() pageChange = new EventEmitter<PageEvent>();

  public dataSource: MatTableDataSource<T>;
  public displayedColumns: string[] = [];
  public skeletonData: any[] = [];

  constructor() {
    this.dataSource = new MatTableDataSource<T>([]);
  }

  ngOnInit(): void {
    this.initializeTable();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['columns'] || changes['data'] || changes['loading']) {
      this.initializeTable();
    }
    if (changes['totalCount'] && this.showPagination) {
      this.setupPagination();
    }
  }

  private initializeTable(): void {
    this.setupColumns();
    this.setupDataSource();
    this.setupSkeleton();
    if (this.showPagination) {
      this.setupPagination();
    }
  }

  private setupColumns(): void {
    if (this.columns && this.columns.length > 0) {
      const sortedColumns = [...this.columns].sort((a, b) => {
        const orderA = a.order ?? Number.MAX_SAFE_INTEGER;
        const orderB = b.order ?? Number.MAX_SAFE_INTEGER;
        return orderA - orderB;
      });

      this.displayedColumns = sortedColumns.map(col => col.elementKey as string);
    }
  }

  private setupDataSource(): void {
    if (this.loading) {
      this.dataSource.data = [];
    } else {
      this.dataSource.data = this.data || [];
    }

    if (this.sort) {
      this.dataSource.sort = this.sort;
    }

    if (this.sortFn) {
      this.dataSource.sortingDataAccessor = (data: T, sortHeaderId: string): string | number => {
        const value = data[sortHeaderId as keyof T];
        if (typeof value === 'string') {
          return value.toLowerCase();
        }
        return (value as unknown) as string | number;
      };
    }
  }

  private setupSkeleton(): void {
    if (this.loading) {
      this.skeletonData = Array(this.skeletonRows).fill({}).map((_, index) => {
        const skeletonRow: any = { id: `skeleton-${index}` };
        this.displayedColumns.forEach(col => {
          skeletonRow[col] = '';
        });
        return skeletonRow;
      });
    }
  }

  public onCellClick(event: MouseEvent, element: T, column: ColumnConfig<T>): void {
    if (column.onClick && typeof column.onClick === 'function') {
      column.onClick(event, element);
    }
  }

  public getColumnConfig(columnKey: string): ColumnConfig<T> | undefined {
    return this.columns.find(col => col.elementKey === columnKey);
  }

  public getCellValue(element: T, columnKey: string): any {
    let value: any = element;
    for (let key of columnKey.split(".")) {
      value = value?.[key];
    }
    return value;
  }

  public onSortChange(sortState: Sort): void {
    this.sortChange.emit(sortState);
  }

  public onPageChange(pageEvent: PageEvent): void {
    this.pageChange.emit(pageEvent);
  }

  private setupPagination(): void {
    if (this.paginator) {
      console.log('Setting up paginator with totalCount:', this.totalCount);
      this.paginator.length = this.totalCount;
      this.paginator.pageSize = this.pageSize;
      this.paginator.pageSizeOptions = this.pageSizeOptions;
    }
  }
}
